import type { AreaHit, AreaListItem } from "@/types/area";

const isoOf = (seconds: number | undefined): string | null =>
  seconds === undefined ? null : new Date(seconds * 1000).toISOString();

/** An `/areas/explore` hit as the list row the areas pages work with. */
export const toAreaListItem = ({ document: d }: AreaHit): AreaListItem => ({
  id: d.id,
  city: d.city,
  countryCode: d.countryCode,
  name: d.name,
  kind: d.kind,
  parentId: d.parentId ?? null,
  partOrder: d.partOrder ?? null,
  origin: d.origin ?? "",
  venueCount: d.venueCount,
  aliases: d.aliases ?? [],
  displayNames: d.displayNames ?? {},
  centre: d.centre ? { latitude: d.centre[0], longitude: d.centre[1] } : null,
  createdAt: isoOf(d.createdAt),
  updatedAt: isoOf(d.updatedAt),
});

/**
 * `select` for `useList` / `useTable`: swap the hits for list rows and keep `total` and `facets`.
 * Module-level so its identity is stable between renders.
 */
export const selectAreaRows = <T extends { data: unknown[] }>(
  response: T,
): Omit<T, "data"> & { data: AreaListItem[] } => ({
  ...response,
  data: (response.data as AreaHit[]).map(toAreaListItem),
});

export interface FacetCount {
  value: string;
  count: number;
}

/** The counts of one facet field of an explore response, or none when it is absent. */
export const facetCounts = (facets: unknown, field: string): FacetCount[] => {
  if (!Array.isArray(facets)) return [];
  const hit = (facets as { field_name?: string; counts?: FacetCount[] }[]).find(
    (f) => f.field_name === field,
  );
  return hit?.counts ?? [];
};
